import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';

import { BadgingService } from 'app/modules/social/services/badging/badging.service';
import { Loggable } from 'app/utils/logging/loggable';
import Image from 'entity/Image';
import RegistrationField from 'entity/RegistrationField';
import SocialEvent from 'entity/SocialEvent';
import SocialEventEmail from 'entity/SocialEventEmail';
import UserRegistration from 'entity/UserRegistration';
import UserRegistrationValue from 'entity/UserRegistrationValue';
import QuestionInputType from 'enums/QuestionInputType';
import SocialEventEmailType from 'enums/SocialEventEmailType';
import ImageController from 'rest/ImageController';
import Paging from 'rest/Paging';
import SocialEventController from 'rest/SocialEventController';

import { defaultRegistrationFieldsNames } from '../pages/registration-form-page/registration-form-page.component';

export interface RegistrationFieldQuestions extends RegistrationField {
  inputType?: QuestionInputType;
  options?: string[];
}

export interface UserRegistrationPassport {
  registrationId?: string;
  endDateTime?: Date;
}

@Injectable({
  providedIn: 'root',
})
export class EventsService extends Loggable {
  private eventsController: SocialEventController;
  public get registrationId(): string | null {
    const storageRegistration = window.localStorage.getItem('registrationId');

    if (!storageRegistration) {
      return storageRegistration;
    }

    try {
      const { endDateTime, registrationId }: UserRegistrationPassport = JSON.parse(storageRegistration);

      if (!endDateTime || !registrationId) {
        return null;
      }

      const eventEndDate = new Date(endDateTime);
      const now = new Date();

      if (eventEndDate < now) {
        this.registrationId = undefined;
        return null;
      }

      return registrationId;
    } catch {
      this.registrationId = undefined;
      return null;
    }
  }
  public set registrationId(value: UserRegistrationPassport | undefined) {
    if (value) {
      window.localStorage.setItem('registrationId', JSON.stringify(value));
    } else {
      window.localStorage.removeItem('registrationId');
    }
  }

  public get eventName(): string | undefined {
    return window.localStorage.getItem('eventName') || undefined;
  }
  public set eventName(value: string | undefined) {
    if (value) {
      window.localStorage.setItem('eventName', value);
    } else {
      window.localStorage.removeItem('eventName');
    }
  }

  public get eventFlowStep(): string | undefined {
    return window.localStorage.getItem('eventFlowStep') || undefined;
  }
  public set eventFlowStep(value: string | undefined) {
    if (value) {
      window.localStorage.setItem('eventFlowStep', value);
    } else {
      window.localStorage.removeItem('eventFlowStep');
    }
  }

  public get eventId(): string | undefined {
    return window.localStorage.getItem('eventId') || undefined;
  }
  public set eventId(value: string | undefined) {
    if (value) {
      window.localStorage.setItem('eventId', value);
    } else {
      window.localStorage.removeItem('eventId');
    }
  }

  public constructor(private badgingService: BadgingService) {
    super();
    this.eventsController = new SocialEventController();
  }

  public getEvents(
    standardView: boolean,
    pastEvents: boolean,
    text?: string,
    page?: Paging,
  ): Observable<SocialEvent[]> {
    return from(this.eventsController.getEvents(standardView, pastEvents, text, page));
  }

  public getRegistrants(socialEventId: string, searchQuery?: string, page?: Paging): Observable<UserRegistration[]> {
    return from(this.eventsController.getRegistrations(socialEventId, searchQuery, page));
  }

  public confirmRegistrations(socialEventId: string, userIds: string[], all?: boolean): Observable<number> {
    return from(this.eventsController.confirmRegistrations(socialEventId, all || false, userIds));
  }

  public createEvent(socialEvent: SocialEvent): Observable<SocialEvent> {
    return from(this.eventsController.createEvent(socialEvent));
  }

  public updateEvent(socialEvent: SocialEvent): Observable<SocialEvent> {
    return from(this.eventsController.updateEvent(socialEvent));
  }

  public deletedEvent(socialEventId: string): Observable<void> {
    return from(this.eventsController.deleteEvent(socialEventId));
  }

  public uploadImage(
    imageName: string,
    type: string,
    width: number,
    height: number,
    base64: string,
  ): Observable<Image> {
    return from(new ImageController().uploadBase64Image(imageName, type, width, height, base64));
  }

  public getRegistration(registrationId: string | null): Observable<UserRegistration> {
    if (!registrationId) {
      return from(Promise.resolve({} as UserRegistration));
    }
    return from(this.eventsController.getRegistration(registrationId).catch(() => ({}) as UserRegistration));
  }

  public getRegistrantInfo(registrationId: string): Promise<UserRegistration> {
    return this.eventsController.getRegistration(registrationId);
  }

  public transferRegistration(registrationId: string): Promise<void> {
    return this.eventsController.transferRegistration(registrationId);
  }

  public getRegistrationCount(socialEventId: string): Observable<number> {
    return from(this.eventsController.getRegistrationCount(socialEventId));
  }

  public registrationCount(socialEventId: string): Promise<number> {
    return this.eventsController.getRegistrationCount(socialEventId);
  }

  public getEventEmails(socialEventId: string): Observable<SocialEventEmail[]> {
    return from(this.eventsController.getEventEmails(socialEventId));
  }

  public saveEventEmail(socialEventId: string, email: SocialEventEmail): Promise<SocialEventEmail> {
    return this.eventsController.saveEventEmail(socialEventId, email);
  }

  public getEvent(socialEventId: string): Observable<SocialEvent> {
    return from(this.eventsController.getEvent(socialEventId));
  }

  public getRegistrationFields(socialEventId: string): Observable<RegistrationField[]> {
    return from(this.eventsController.getRegistrationFields(socialEventId));
  }

  public saveRegistrationFields(socialEventId: string, fields: RegistrationField[]) {
    return this.eventsController.saveRegistrationFields(socialEventId, fields);
  }

  public saveRegistration(socialEventId: string, values: UserRegistrationValue[]) {
    return from(this.eventsController.saveRegistration(socialEventId, values));
  }

  public sendTestEmail(socialEventId: string, type: SocialEventEmailType, email?: string): Promise<void> {
    return this.eventsController.sendTestEmail(socialEventId, type, email);
  }

  public getEventEmailTemplate(type: SocialEventEmailType): Observable<string> {
    return from(this.eventsController.getEventEmailTemplate(type));
  }

  public saveEventEmails(socialEventId: string, emails: SocialEventEmail[]): Promise<SocialEventEmail[]> {
    return this.eventsController.saveEventEmails(socialEventId, emails);
  }

  public getEventsByLink(eventLink: string): Promise<SocialEvent> {
    return this.eventsController.getEventByLink(eventLink);
  }

  public isLinkAvailable(eventLink: string): Promise<boolean> {
    return this.eventsController.isLinkAvailable(eventLink);
  }

  public async getFormRegistrationField(event: SocialEvent): Promise<RegistrationFieldQuestions[] | undefined> {
    if (!event.socialEventId) {
      this.error('Error while fetching Registration Fields, socialEventId is undefined');
      return;
    }

    if (!event.socialRoomSetId) {
      this.error('Error while fetching Registration Fields, socialRoomSetId is undefined');
      return;
    }

    try {
      const [registrationField, badgingQuestions] = await Promise.all([
        this.eventsController.getRegistrationFields(event.socialEventId),
        this.badgingService.getBadgeQuestions(event.socialRoomSetId),
      ]);

      const registrationFieldForm: RegistrationFieldQuestions[] = [];
      registrationField.forEach((registration) => {
        const badgeQuestion = badgingQuestions.find((question) => question.title === registration.name);
        if (badgeQuestion || defaultRegistrationFieldsNames.includes(registration.name?.toLowerCase() ?? '')) {
          registrationFieldForm.push({
            ...registration,
            inputType: badgeQuestion?.inputType,
            options: badgeQuestion?.options,
            description: badgeQuestion?.description,
          });
        }
      });

      return registrationFieldForm;
    } catch (err) {
      this.error('Error while fetching questions', err);
    }
  }
}
